import { useState } from "react";
import { createUserWithEmailAndPassword, updateProfile, sendEmailVerification } from "firebase/auth";
import { auth } from "../../lib/firebase";
import { createCoachDocument } from "../../lib/firestore";

const SignUp = ({ onSwitchToLogin }) => {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    password: "",
    confirmPassword: "",
    teamId: "",
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = "Full name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Please confirm your password";
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    if (!formData.teamId.trim()) {
      newErrors.teamId = "Team ID is required";
    } else if (!/^[a-zA-Z0-9]+$/.test(formData.teamId)) {
      newErrors.teamId = "Team ID must contain only letters and numbers";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);

    try {
      // Create Firebase Auth user
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );

      // Update the user's display name
      await updateProfile(userCredential.user, {
        displayName: formData.fullName,
      });

      // Create coach document in Firestore
      await createCoachDocument(userCredential.user.uid, {
        email: formData.email,
        fullName: formData.fullName,
        teamId: formData.teamId,
      });

      // Send email verification
      await sendEmailVerification(userCredential.user);

      await auth.signOut();

      // Now show the verification screen
      setVerificationSent(true);
    } catch (error) {
      let errorMessage = "Failed to create account. Please try again.";

      if (error.code === "auth/email-already-in-use") {
        errorMessage = "This email address is already registered.";
      } else if (error.code === "auth/weak-password") {
        errorMessage =
          "Password is too weak. Please choose a stronger password.";
      } else if (error.code === "auth/invalid-email") {
        errorMessage = "Please enter a valid email address.";
      }

      setErrors({ submit: errorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  // Show verification sent message
  if (verificationSent) {
    return (
      <div className="page-container">
        <div className="auth-card">
          <div className="auth-header">
            <div className="auth-logo-container">
              <img src={`${process.env.PUBLIC_URL}/images/logo-playfast.svg`} alt="PlayFast Logo" className="auth-logo-image" />
            </div>
            <h2 className="auth-title">Verify Your Email</h2>
            <p className="auth-subtitle">
              We've sent a verification link to <strong>{formData.email}</strong>
            </p>
          </div>
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <p style={{ color: '#666', marginBottom: '20px' }}>
              Please check your inbox and click the verification link to activate your account.
            </p>
            <p style={{
              color: '#d97706',
              fontSize: '15px',
              fontWeight: '500',
              margin: 0
            }}>
              💡 Didn't receive the email? <strong>Check your spam/junk folder.</strong>
            </p>
          </div>
          <button
            type="button"
            onClick={onSwitchToLogin}
            className="btn btn-primary"
            style={{ width: '100%' }}
          >
            Go to Signin
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="auth-card">
        {/* Header */}
        <div className="auth-header">
          <div className="auth-logo-container">
            <img src={`${process.env.PUBLIC_URL}/images/logo-playfast.svg`} alt="PlayFast Logo" className="auth-logo-image" />
          </div>
          <h2 className="auth-title">Create Coach Account</h2>
          <p className="auth-subtitle">Join PlayFast to manage your team</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          {/* Full Name */}
          <div className="form-group">
            <label htmlFor="fullName" className="form-label">
              Full Name
            </label>
            <input
              type="text"
              id="fullName"
              name="fullName"
              value={formData.fullName}
              onChange={handleChange}
              autoComplete="name"
              className={`form-input ${errors.fullName ? "form-input-error" : ""
                }`}
              placeholder="Enter your full name"
            />
            {errors.fullName && <p className="form-error">{errors.fullName}</p>}
          </div>

          {/* Email */}
          <div className="form-group">
            <label htmlFor="email" className="form-label">
              Email Address
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              autoComplete="email"
              className={`form-input ${errors.email ? "form-input-error" : ""}`}
              placeholder="Enter your email"
            />
            {errors.email && <p className="form-error">{errors.email}</p>}
          </div>

          {/* Password */}
          <div className="form-group">
            <label htmlFor="password" className="form-label">
              Password
            </label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              autoComplete="new-password"
              className={`form-input ${errors.password ? "form-input-error" : ""
                }`}
              placeholder="Create a password"
            />
            {errors.password && <p className="form-error">{errors.password}</p>}
          </div>

          {/* Confirm Password */}
          <div className="form-group">
            <label htmlFor="confirmPassword" className="form-label">
              Confirm Password
            </label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              autoComplete="new-password"
              className={`form-input ${errors.confirmPassword ? "form-input-error" : ""
                }`}
              placeholder="Confirm your password"
            />
            {errors.confirmPassword && (
              <p className="form-error">{errors.confirmPassword}</p>
            )}
          </div>

          {/* Team ID */}
          <div className="form-group">
            <label htmlFor="teamId" className="form-label">
              Team ID
            </label>
            <input
              type="text"
              id="teamId"
              name="teamId"
              value={formData.teamId}
              onChange={handleChange}
              className={`form-input ${errors.teamId ? "form-input-error" : ""
                }`}
              placeholder="Enter your team ID (letters and numbers only)"
            />
            {errors.teamId && <p className="form-error">{errors.teamId}</p>}
          </div>

          {/* Submit Error */}
          {errors.submit && (
            <div className="form-submit-error">
              <p className="form-submit-error-text">{errors.submit}</p>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading}
            className="btn btn-primary form-submit-button"
          >
            {isLoading && <div className="spinner spinner-small"></div>}
            {isLoading ? "Creating Coach Account..." : "Create Coach Account"}
          </button>
        </form>

        {/* Login Link */}
        <div className="auth-footer">
          <p className="auth-footer-text">
            Already have an account?{" "}
            <button onClick={onSwitchToLogin} className="auth-link">
              Sign in here
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignUp;
